/**
 * Pure helpers for the Changelog browser watermark (localStorage). Shared by the badge control and
 * the mark-seen page behaviour so unit tests can inject a Storage-like double.
 *
 * Attention-mark rule: keep in lockstep with C# `ChangelogBadgeRule` in mvdmio.ASP (unit-test matrix
 * there) and the TS cases in changelogWatermark.test.ts. Production UI uses this TS port only —
 * the watermark lives in the browser, so SSR cannot decide the dot. C# remains the documented pure
 * rule for server-side tests / future use; do not change one without the other.
 */

export interface StorageLike {
   getItem(key: string): string | null;
   setItem(key: string, value: string): void;
}

/** Dispatched on window after mark-seen so mounted badges refresh without remount (Turbo morph). */
export const CHANGELOG_SEEN_EVENT = "changelog:seen";

export interface ChangelogSeenDetail {
   app: string;
}

/**
 * Same rule as C# ChangelogBadgeRule (identity form): show the attention mark when there is a
 * newest identity and the watermark is missing or does not equal it.
 */
export function shouldShowAttentionMark(
   newestIdentity: string | null | undefined,
   browserWatermark: string | null | undefined,
): boolean {
   if (!newestIdentity) return false;
   return !browserWatermark || browserWatermark !== newestIdentity;
}

/** Writes the newest entry identity as the seen watermark. No-op when newestIdentity is empty. */
export function markSeen(
   storage: StorageLike,
   app: string,
   newestIdentity: string | null | undefined,
): void {
   if (!newestIdentity) return;
   storage.setItem(`mvdmio.${app}.changelog.seenIdentity`, newestIdentity);
}

/**
 * Notify any mounted `changelog-badge` controllers for this app to re-run their rule.
 * Call after a successful mark-seen write so the red dot clears while the layout control stays mounted.
 */
export function notifyChangelogSeen(app: string): void {
   if (typeof window === "undefined") return;
   window.dispatchEvent(
      new CustomEvent<ChangelogSeenDetail>(CHANGELOG_SEEN_EVENT, {
         detail: { app },
      }),
   );
}

export function readWatermark(storage: StorageLike, app: string): string | null {
   return storage.getItem(`mvdmio.${app}.changelog.seenIdentity`);
}
